const BASE = process.env.REACT_APP_API_BASE_URL;

const authHeaders = (json = true) => {
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const handleResponse = async (res, errMsg) => {
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.message || ''; } catch {}
    throw new Error(detail || errMsg);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const getNotifications = () =>
  fetch(`${BASE}/api/notifications`, { headers: authHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load notifications'));

export const getUnreadCount = () =>
  fetch(`${BASE}/api/notifications/unread-count`, { headers: authHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load unread count'));

export const markNotificationRead = (id) =>
  fetch(`${BASE}/api/notifications/${id}/read`, {
    method: 'POST',
    headers: authHeaders(),
  }).then((r) => handleResponse(r, 'Failed to mark notification read'));

export const markAllNotificationsRead = () =>
  fetch(`${BASE}/api/notifications/read-all`, {
    method: 'POST',
    headers: authHeaders(),
  }).then((r) => handleResponse(r, 'Failed to mark all read'));

// ─── AUTH: Forgot Password ────────────────────────────────────
export const forgotPassword = (email) =>
  fetch(`${BASE}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  }).then((r) => handleResponse(r, 'Failed to send OTP'));

export const verifyOtp = (email, otpCode) =>
  fetch(`${BASE}/api/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otpCode }),
  }).then((r) => handleResponse(r, 'Invalid or expired OTP'));

export const resetPassword = (email, otpCode, newPassword) =>
  fetch(`${BASE}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otpCode, newPassword }),
  }).then((r) => handleResponse(r, 'Failed to reset password'));

export const resendConfirmation = (email) =>
  fetch(`${BASE}/api/auth/resend-confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  }).then((r) => handleResponse(r, 'Failed to resend confirmation email'));

export const getMe = () =>
  fetch(`${BASE}/api/auth/me`, { headers: authHeaders(false) }).then(async (r) => {
    if (!r.ok) {
      let detail = '';
      try { detail = (await r.json())?.message || ''; } catch {}
      throw new Error(detail || `Failed to load user (status ${r.status})`);
    }
    if (r.status === 204) return null;
    const text = await r.text();
    return text ? JSON.parse(text) : null;
  });

// ─── ADMIN: Email Templates ───────────────────────────────────
const adminAuthHeaders = (json = true) => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

export const adminGetEmailTemplates = () =>
  fetch(`${BASE}/api/admin/email-templates`, { headers: adminAuthHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load templates'));

export const adminUpsertEmailTemplate = (data) =>
  fetch(`${BASE}/api/admin/email-templates`, {
    method: 'POST',
    headers: adminAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => handleResponse(r, 'Failed to save template'));

export const adminToggleEmailTemplate = (id) =>
  fetch(`${BASE}/api/admin/email-templates/${id}/toggle`, {
    method: 'PATCH',
    headers: adminAuthHeaders(),
  }).then((r) => handleResponse(r, 'Failed to toggle template'));

export const adminPreviewEmailTemplate = (slug, placeholders) =>
  fetch(`${BASE}/api/admin/email-templates/preview`, {
    method: 'POST',
    headers: adminAuthHeaders(),
    body: JSON.stringify({ slug, placeholders }),
  }).then(async (r) => {
    if (r.status === 401) {
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (!r.ok) {
      let detail = '';
      try { detail = (await r.json())?.message || ''; } catch {}
      throw new Error(detail || 'Failed to preview template');
    }
    return r.text();
  });

export const adminSeedEmailTemplates = () =>
  fetch(`${BASE}/api/admin/email-templates/seed`, {
    method: 'POST',
    headers: adminAuthHeaders(),
  }).then((r) => handleResponse(r, 'Failed to seed templates'));

export const adminSendNewsletter = (slug, subject) =>
  fetch(`${BASE}/api/admin/email-templates/newsletter`, {
    method: 'POST',
    headers: adminAuthHeaders(),
    body: JSON.stringify({ slug, subject }),
  }).then((r) => handleResponse(r, 'Failed to send newsletter'));

// ─── ADMIN: Email Logs ────────────────────────────────────────
export const adminGetEmailLogs = ({ page = 1, pageSize = 20, status, slug, search } = {}) => {
  const params = new URLSearchParams({ page, pageSize });
  if (status) params.set('status', status);
  if (slug) params.set('slug', slug);
  if (search) params.set('search', search);
  return fetch(`${BASE}/api/admin/email-logs?${params}`, { headers: adminAuthHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load email logs'));
};

export const adminGetEmailLogDetail = (id) =>
  fetch(`${BASE}/api/admin/email-logs/${id}`, { headers: adminAuthHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load email log'));

export const adminGetEmailLogStats = () =>
  fetch(`${BASE}/api/admin/email-logs/stats`, { headers: adminAuthHeaders(false) })
    .then((r) => handleResponse(r, 'Failed to load email stats'));
